import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ButtonIcon,
  IconMenu,
  IconX,
  NavBar,
  NavBarList,
  NavBarListItem,
  Type,
} from '@spotify-internal/encore-creator-web';
import {
  gray10,
  white,
  spacer8,
  spacer16,
  spacer20,
  spacer24,
  spacer48,
  screenMdMin,
} from '@spotify-internal/encore-foundation';
import SpotifyForPodcastersMobileLogo from '../../svg/podcasters-mobile-logo.svg';
import { SpotifyForPodcastersLogo } from '../../vendor/SpotifyForPodcastersLogo';
import { pageConstraintStyles } from './styles';
import { useMe } from '../../hooks/useMe';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { PODCASTERS_ZENDESK_LINK } from '../../lib/constants';
import { useLocalePreferenceContext } from '../../hooks/useLocalePreference';
import { login } from '../../lib/Request';

export function Header() {
  const me = useMe();
  const [showPanel, setShowPanel] = useState(false);
  const intl = useIntl();
  const localePreference = useLocalePreferenceContext();

  return (
    <>
      <HeaderContainer>
        <Logo>
          <SpotifyForPodcastersLogo />
        </Logo>
        <MobileLogo src={SpotifyForPodcastersMobileLogo} />
        <HeaderNavBar
          list={
            <NavBarList>
              <HeaderNavItem
                label={intl.formatMessage(
                  defineMessage({
                    defaultMessage: 'News',
                    id: 'landing-page.nav.news',
                    description: 'Link to the blog',
                  }),
                )}
                href={`${window.location.origin}/blog`}
                style={{ color: white }}
                sentenceCase
              />
              <HeaderNavItem
                label="Promo Cards"
                href={`${window.location.origin}/promocards`}
                style={{ color: white }}
                sentenceCase
              />
              <HeaderNavItem
                label={intl.formatMessage(
                  defineMessage({
                    defaultMessage: 'FAQ',
                    id: 'landing-page.nav.faq',
                    description: 'Link to Frequently Asked Questions',
                  }),
                )}
                href={PODCASTERS_ZENDESK_LINK + localePreference.languageAndRegionCode}
                target="_blank"
                style={{ color: white }}
                sentenceCase
              />
              {/* TODO: Should be a button not anchor */}
              <HeaderNavItem
                role="button"
                onClick={login}
                label={
                  me
                    ? intl.formatMessage(
                        defineMessage({
                          defaultMessage: 'Your Dashboard',
                          id: 'landing-page.nav.dashboard',
                          description: 'Link to the main main stats view',
                        }),
                      )
                    : intl.formatMessage(
                        defineMessage({
                          defaultMessage: 'Log In',
                          id: 'landing-page.nav.log-in',
                          description: 'Link to the log in view',
                        }),
                      )
                }
                style={{ color: white }}
                sentenceCase
              />
            </NavBarList>
          }
        />
        <MobileHamburger aria-label="IconMenu" onClick={() => setShowPanel(true)} />
      </HeaderContainer>
      {showPanel && (
        <NavPanel data-testid="landing-page--nav-panel">
          <ButtonIcon
            onClick={() => setShowPanel(false)}
            style={{ position: 'absolute', top: spacer24, right: spacer16 }}
          >
            <IconX aria-label="Close" color={white} />
          </ButtonIcon>
          <PanelLink href={`${window.location.origin}/blog`}>
            <PanelLinkText>
              <FormattedMessage id="landing-page.header.news" defaultMessage="News" />
            </PanelLinkText>
          </PanelLink>
          <PanelLink href={`${window.location.origin}/promocards`}>
            <PanelLinkText>Promo Cards</PanelLinkText>
          </PanelLink>
          <PanelLink
            href={PODCASTERS_ZENDESK_LINK + localePreference.languageAndRegionCode}
            target="_blank"
          >
            <PanelLinkText>
              <FormattedMessage id="landing-page.header.FAQ" defaultMessage="FAQ" />
            </PanelLinkText>
          </PanelLink>
          <PanelButton onClick={login}>
            <PanelLinkText>
              {me ? (
                <FormattedMessage
                  id="landing-page.header.your-dashboard"
                  defaultMessage="Your Dashboard"
                />
              ) : (
                <FormattedMessage id="landing-page.header.log-in" defaultMessage="Log In" />
              )}
            </PanelLinkText>
          </PanelButton>
        </NavPanel>
      )}
    </>
  );
}

const HeaderContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: ${spacer16} ${spacer16} 0;

  @media (min-width: ${screenMdMin}) {
    ${pageConstraintStyles}

    padding: 0 ${spacer48};
  }
`;

const Logo = styled.div`
  width: 280px;
  height: 40px;
  display: none;

  @media (min-width: ${screenMdMin}) {
    display: block;
  }
`;

const MobileLogo = styled.img`
  @media (min-width: ${screenMdMin}) {
    display: none;
  }
`;

const HeaderNavBar = styled(NavBar)`
  display: none;

  @media (min-width: ${screenMdMin}) {
    display: block;
    padding: ${spacer20} 0 ${spacer8};
  }
`;

const HeaderNavItem = styled(NavBarListItem).attrs({
  sentenceCase: true,
})`
  color: ${white};
`;

const MobileHamburger = styled(IconMenu)`
  @media (min-width: ${screenMdMin}) {
    display: none;
  }
`;

const NavPanel = styled.div`
  background-color: ${gray10};
  width: 100%;
  height: 100%;
  padding: 38px;
  position: fixed;
  top: 0;
  z-index: 10;
`;

const PanelLink = styled.a`
  color: ${white};
`;

const PanelButton = styled.button`
  color: ${white};
`;

const PanelLinkText = styled(Type).attrs({
  forwardedAs: 'p',
  weight: Type.bold,
})`
  font-size: 36px;
  line-height: 1;
`;
